import { FahrlehrerCh } from '@/src/@types/blinkadmin'

export type CrosslinkOffer = 'lessons' | 'firstaid' | 'vku' | 'pgs' | 'wab'

export type CrosslinksData = {
  currentOffer: CrosslinkOffer
  nearbyLocations: FahrlehrerCh.LocationWithDistance[]
  otherOffers: FahrlehrerCh.OtherOffers
  /** Location of the current page; offers at the same location show no distance. */
  locationId: number | null
}

export type CrosslinkRow = {
  key: string
  label: string
  /** `null` when the offer is at the page's own location. */
  distanceKm: number | null
  href: string
}

const OFFER_ORDER: (keyof FahrlehrerCh.OtherOffers)[] = [
  'lessons',
  'firstaid',
  'vku',
  'pgs',
]

const OFFER_LABELS: Record<CrosslinkOffer, string> = {
  lessons: 'Fahrlehrer:innen',
  firstaid: 'Nothelferkurs',
  vku: 'VKU',
  pgs: 'Motorradgrundkurs',
  wab: 'WAB Kurs',
}

export const OFFER_NEARBY_HEADINGS: Record<
  CrosslinkOffer,
  { title: string; subtitle: string }
> = {
  lessons: {
    title: 'Fahrlehrer:innen in der Nähe',
    subtitle: 'Weitere Fahrlehrer:innen in deiner Region',
  },
  firstaid: {
    title: 'Nothelferkurse in der Nähe',
    subtitle: 'Weitere Kursorte für deinen Nothelferkurs',
  },
  vku: {
    title: 'VKU in der Nähe',
    subtitle: 'Weitere Kursorte für den Verkehrskundeunterricht (VKU)',
  },
  pgs: {
    title: 'Motorradgrundkurse in der Nähe',
    subtitle: 'Weitere Kursorte für deinen Motorradgrundkurs',
  },
  wab: {
    title: 'WAB Kurse in der Nähe',
    subtitle: 'Weitere Kursorte für deinen WAB Kurs',
  },
}

function distanceFor(
  data: CrosslinksData,
  location: FahrlehrerCh.LocationWithDistance
): number | null {
  return location.locationId === data.locationId ? null : location.distanceKm
}

export function getNearbyRows(data: CrosslinksData): CrosslinkRow[] {
  const label = OFFER_LABELS[data.currentOffer]
  return data.nearbyLocations.map((location) => ({
    key: String(location.locationId),
    label: `${label} in ${location.locationName}`,
    distanceKm: distanceFor(data, location),
    href: location.locationSlug,
  }))
}

export function getOtherOfferRows(data: CrosslinksData): CrosslinkRow[] {
  const rows: CrosslinkRow[] = []
  for (const offer of OFFER_ORDER) {
    if (offer === data.currentOffer) continue
    const location = data.otherOffers[offer]
    if (!location) continue
    rows.push({
      key: offer,
      label: `${OFFER_LABELS[offer]} in ${location.locationName}`,
      distanceKm: distanceFor(data, location),
      href: location.locationSlug,
    })
  }
  return rows
}

export function getOtherOffersHeading(
  data: CrosslinksData,
  locationName: string
): { title: string; subtitle: string } {
  if (data.currentOffer === 'lessons') {
    return {
      title: `Kurse in der Nähe von ${locationName}`,
      subtitle: 'Obligatorische Kurse für deinen Führerausweis',
    }
  }
  return {
    title: `Weitere Angebote in der Nähe von ${locationName}`,
    subtitle: 'Alles rund um deine Fahrausbildung',
  }
}
